import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { PiMagnifyingGlassLight, PiUsersFourLight } from 'react-icons/pi';
import ProfileImage from '../Header/ProfileImage';
import { motion } from 'framer-motion';
import { formatTime } from '../../utils/functions';

interface NewChatListProps {
  chatList: any[];
  selectedChat: string | null;
  onChatSelect: (id: string, chat: any) => void;
  unreadMessages: Record<string, number>;
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
}

const GroupAvatar = ({ grp }: { grp: any }) => {
  const members = grp.GroupData?.Members ?? [];
  if (!members.length) {
    return (
      <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center text-white mr-3">
        <PiUsersFourLight className="text-lg" />
      </div>
    );
  }
  const [a, b] = members;

  const getMemberProps = (member: any) => ({
    profilePicture: member.ProfilePicture || null,
    email: member.Email,
    initials: member.Initials || `${member.FirstName?.[0] || ''}${member.LastName?.[0] || ''}`,
    name: member.Name,
    firstName: member.FirstName,
    lastName: member.LastName,
    size: 'h-8 w-8',
  });

  return (
    <div className="flex -space-x-2">
      {b && <ProfileImage {...getMemberProps(b)} />}
      {a && <ProfileImage {...getMemberProps(a)} />}
    </div>
  );
};

const NewChatList: React.FC<NewChatListProps> = ({
  chatList,
  selectedChat,
  onChatSelect,
  unreadMessages,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const userInfo = useSelector((s: RootState) => s.user.userInfo);

  const userUID = userInfo?.UserUID || userInfo?.uuid_encrypted || (userInfo?.UserID ? String(userInfo.UserID) : undefined);

  const filteredChats = useMemo(() => {
    if (!searchTerm.trim()) return chatList;

    return chatList.filter((chat: any) => {
      const displayName = chat.isGroup
        ? chat.GroupData?.Name || 'Group'
        : `${chat.userData?.FirstName} ${chat.userData?.LastName}`.trim();

      return displayName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [chatList, searchTerm]);

  const getChatDisplayName = (chat: any) => {
    if (chat.isGroup) {
      return chat.GroupData?.Name || 'Group';
    }
    return `${chat.userData?.FirstName} ${chat.userData?.LastName}`.trim();
  };

  const getChatAvatar = (chat: any) => {
    if (chat.isGroup) {
      return <GroupAvatar grp={chat} />;
    }

    return (
      <ProfileImage
        profilePicture={chat.userData?.ProfilePicture || null}
        email={chat.userData?.Email}
        initials={chat.userData?.Initials || `${chat.userData?.FirstName?.[0] || ''}${chat.userData?.LastName?.[0] || ''}`}
        name={getChatDisplayName(chat)}
        firstName={chat.userData?.FirstName}
        lastName={chat.userData?.LastName}
        size="h-10 w-10"
      />
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{t('Messages')}</h2>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <PiMagnifyingGlassLight className="absolute left-3 top-2.5 text-gray-400" />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading chats...</div>
        ) : filteredChats.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">{t('No chats found')}</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredChats.map((chat: any) => (
              <motion.button
                key={chat.ChatID}
                onClick={() => onChatSelect(chat.ChatID, chat)}
                whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                className={`w-full p-4 text-left transition-colors ${
                  selectedChat === chat.ChatID
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  {getChatAvatar(chat)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {getChatDisplayName(chat)}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {chat.LastMessageTime && formatTime(chat.LastMessageTime)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {/* {chat.LastMessage || 'No messages yet'} */}
                    </p>
                  </div>
                  {(unreadMessages[chat.ChatID] || 0) > 0 && (
                    <div className="ml-2 bg-primary-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">
                      {Math.min(unreadMessages[chat.ChatID], 9)}
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewChatList;
